import { moneefloApi } from "./client";
import { coreError } from "./core-error";
import { orderRefFromRoute } from "./legacy-forex-order-api";

/**
 * The forex desk's actions on one order, answered by the new backend: coupon,
 * payment link, notes, TCS, delivery charge, deliveries, cards and the
 * customer's saved addresses.
 *
 * Every write names the order from the address bar (`orderRefFromRoute`), not
 * from an id the form carries: the old forms turn "202610-000123" into a
 * number and would act on a different order's reference.
 *
 * Money goes to core in rupees where core asks for rupees (TCS, delivery
 * charge, a coupon's order value) and comes back in paise. A blank is never
 * sent as 0 or null for a number: the field is left out, because core refuses
 * both. The one null core accepts on purpose is "hand TCS or the delivery
 * charge back to the computed amount".
 *
 * Core refuses with a sentence that says what to do ("A senior approver must
 * set it", "Payment has to be received in full before the order goes out").
 * That sentence is what the operator is shown, unchanged.
 */

const base = () => `/orders/${encodeURIComponent(orderRefFromRoute())}`;

/* ------------------------------------------------------------- coupons -- */

export type CouponCheck = {
  valid: boolean;
  /** Rupees. */
  discount: number;
  net: number;
  reason: string | null;
};

/** Is this code good for this amount, and what would it take off. Nothing is applied. */
export async function validateForexCoupon(data: {
  code: string;
  /** Rupees: the order's value before tax and charges. */
  amount: number;
  customerId?: string;
}): Promise<CouponCheck> {
  try {
    const { data: result } = await moneefloApi.get<{
      valid: boolean;
      discountMinor: string | null;
      netMinor: string | null;
      reason: string | null;
    }>("/coupons/validate", {
      params: {
        code: data.code.trim(),
        amount: data.amount,
        service: "forex",
        ...(data.customerId ? { customerId: data.customerId } : {}),
      },
    });
    return {
      valid: result.valid,
      discount: Number(result.discountMinor ?? 0) / 100,
      net: Number(result.netMinor ?? 0) / 100,
      reason: result.reason,
    };
  } catch (error) {
    throw coreError(error, "The coupon could not be checked.");
  }
}

export async function applyForexCoupon(code: string) {
  try {
    const { data } = await moneefloApi.post(`${base()}/coupon`, { code: code.trim() });
    return data;
  } catch (error) {
    throw coreError(error, "The coupon could not be applied.");
  }
}

export async function removeForexCoupon() {
  try {
    const { data } = await moneefloApi.delete(`${base()}/coupon`);
    return data;
  } catch (error) {
    throw coreError(error, "The coupon could not be removed.");
  }
}

/* -------------------------------------------------------- payment link -- */

export type PaymentLink = {
  id: string;
  url: string;
  amountMinor: string;
  expiresAt: string;
  emailSent: boolean;
};

/** A link the customer pays through. Always returned; emailed only when asked. */
export async function createForexPaymentLink(sendEmail: boolean) {
  try {
    const { data } = await moneefloApi.post<PaymentLink>(`${base()}/payment-link`, { sendEmail });
    return data;
  } catch (error) {
    throw coreError(error, "The payment link could not be created.");
  }
}

export type PaymentLinkStatus = {
  paymentStatus: string;
  dueMinor: string;
  link: {
    id: string;
    amountMinor: string;
    createdAt: string;
    expiresAt: string;
    expired: boolean;
    emailedTo: string | null;
    openedAt: string | null;
    openCount: number;
  } | null;
  attempts: { status: string; at?: string; reference?: string | null; amountMinor?: string }[];
};

export async function getForexPaymentLinkStatus(orderRef?: string) {
  const path = orderRef ? `/orders/${encodeURIComponent(orderRef)}` : base();
  try {
    const { data } = await moneefloApi.get<PaymentLinkStatus>(`${path}/payment-link/status`);
    return data;
  } catch (error) {
    throw coreError(error, "The payment link's status could not be loaded.");
  }
}

/* --------------------------------------------------------------- notes -- */

/** Empty clears the note. */
export async function setForexNotes(notes: string) {
  try {
    const { data } = await moneefloApi.patch(`${base()}/notes`, { notes: notes.trim() || null });
    return data;
  } catch (error) {
    throw coreError(error, "The note could not be saved.");
  }
}

/* ------------------------------------------------- TCS, delivery charge -- */

/**
 * Tax collected at source set by hand, with the reason it is audited under.
 * `amount: null` hands it back to the amount the rules work out. Setting it
 * below that amount needs a senior approver, and core says so.
 */
export async function setForexTcs(data: { amount: number | null; reason: string }) {
  try {
    const { data: result } = await moneefloApi.patch(`${base()}/tcs`, {
      amount: data.amount,
      reason: data.reason.trim(),
    });
    return result;
  } catch (error) {
    throw coreError(error, "The TCS could not be changed.");
  }
}

/** The delivery charge set by hand. `amount: null` hands it back to the city's charge. */
export async function setForexServiceCharge(data: { amount: number | null; reason: string }) {
  try {
    const { data: result } = await moneefloApi.patch(`${base()}/service-charge`, {
      amount: data.amount,
      reason: data.reason.trim(),
    });
    return result;
  } catch (error) {
    throw coreError(error, "The delivery charge could not be changed.");
  }
}

/* ----------------------------------------------------------- deliveries -- */

export type DeliveryMode = "courier" | "self" | "pickup";
export type DeliveryStatusValue =
  | "pending"
  | "ready_to_deliver"
  | "delivered"
  | "failed"
  | "cancelled";

export type DeliveryDestination =
  | { addressId: string }
  | {
      line1: string;
      line2?: string;
      landmark?: string;
      city: string;
      state?: string;
      postalCode: string;
      country?: string;
    };

export async function createForexDelivery(data: {
  mode: DeliveryMode;
  channel?: string;
  /** A pickup has no destination; the other modes need one. */
  destination?: DeliveryDestination;
  items: { lineId: string; fxAmount: number }[];
  scheduledOn?: string;
  assignedTo?: string;
  notes?: string;
}) {
  try {
    const { data: result } = await moneefloApi.post(`${base()}/deliveries`, {
      mode: data.mode,
      channel: data.channel?.trim() || undefined,
      destination: data.mode === "pickup" ? undefined : data.destination,
      items: data.items,
      scheduledOn: data.scheduledOn || undefined,
      assignedTo: data.assignedTo || undefined,
      notes: data.notes?.trim() || undefined,
    });
    return result;
  } catch (error) {
    throw coreError(error, "The delivery could not be created.");
  }
}

/**
 * Change one delivery. A ready or delivered status runs core's release rules:
 * a sale also needs payment in full, and an encashment needs the customs
 * declaration seen. When one is not met core answers with the reason, and the
 * delivery stays as it was.
 */
export async function updateForexDelivery(
  deliveryId: string,
  data: Partial<{
    status: DeliveryStatusValue;
    reason: string;
    channel: string;
    destination: DeliveryDestination;
    scheduledOn: string | null;
    assignedTo: string | null;
    notes: string | null;
  }>,
) {
  const body: Record<string, unknown> = {};
  // These three can be cleared: a blank is sent as an explicit null, not dropped.
  const clearable = ["scheduledOn", "assignedTo", "notes"];
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) body[key] = trimmed;
      else if (clearable.includes(key)) body[key] = null;
    } else if (value !== undefined && value !== null) {
      body[key] = value;
    }
  }
  try {
    const { data: result } = await moneefloApi.patch(
      `${base()}/deliveries/${encodeURIComponent(deliveryId)}`,
      body,
    );
    return result;
  } catch (error) {
    throw coreError(error, "The delivery could not be updated.");
  }
}

/** An encashment's customs declaration on arrival, which payout waits for. */
export async function setDeclaredOnArrival(declared: boolean) {
  try {
    const { data } = await moneefloApi.patch(`${base()}/forex`, {
      encashment: { declaredOnArrival: declared },
    });
    return data;
  } catch (error) {
    throw coreError(error, "The declaration could not be recorded.");
  }
}

/* ---------------------------------------------------------------- cards -- */

export type ForexCardRow = {
  id: string;
  issuer: string | null;
  last4: string;
  status: string;
  expiresOn: string | null;
};

/** The customer's cards on file. Only the last four digits ever come back. */
export async function listForexCards(customerId: string) {
  try {
    const { data } = await moneefloApi.get<{ items: ForexCardRow[] }>("/cards", {
      params: { customerId },
    });
    return data.items;
  } catch (error) {
    throw coreError(error, "The customer's cards could not be loaded.");
  }
}

/** Link a card to a card line: one already on file (`cardId`) or a new one by its number. */
export async function assignForexCard(data: {
  lineId: string;
  cardId?: string;
  cardNumber?: string;
  issuer?: string;
  expiresOn?: string;
}) {
  try {
    const { data: result } = await moneefloApi.post(`${base()}/cards`, {
      lineId: data.lineId,
      cardId: data.cardId || undefined,
      cardNumber: data.cardId ? undefined : data.cardNumber?.replace(/\s+/g, "") || undefined,
      issuer: data.issuer?.trim() || undefined,
      expiresOn: data.expiresOn || undefined,
    });
    return result;
  } catch (error) {
    throw coreError(error, "The card could not be assigned.");
  }
}

/* ------------------------------------------------------------ addresses -- */

export type CustomerAddress = {
  id: string;
  label: string | null;
  line1: string;
  line2: string | null;
  landmark: string | null;
  city: string;
  state: string | null;
  postalCode: string;
  country: string;
  isDefault: boolean;
};

export async function listCustomerAddresses(customerId: string) {
  try {
    const { data } = await moneefloApi.get<{ items: CustomerAddress[] }>(
      `/customers/${encodeURIComponent(customerId)}/addresses`,
    );
    return data.items;
  } catch (error) {
    throw coreError(error, "The customer's addresses could not be loaded.");
  }
}

/* ---------------------------------------------------------------- lines -- */

/**
 * Adding currency lines to the order. Core prices each at its own rate, so no
 * rate is sent. A sale names its product (notes or card); an encashment does
 * not, and core refuses a card there.
 */
/** Some of the lines were added before one was refused; `saved` says which currencies are on the order now. */
export class PartialAddError extends Error {
  saved: string[];
  constructor(message: string, saved: string[]) {
    super(message);
    this.name = "PartialAddError";
    this.saved = saved;
  }
}

export async function addForexLines(data: {
  lines: { currency: string; fxAmount: number; productId?: number }[];
}) {
  const saved: string[] = [];
  for (const line of data.lines) {
    try {
      await moneefloApi.post(`${base()}/lines`, {
        currency: line.currency,
        fxAmount: line.fxAmount,
        ...(line.productId ? { productId: line.productId } : {}),
      });
      saved.push(line.currency);
    } catch (error) {
      const raw = coreError(error, "The currency could not be added.").message;
      // Say which row was refused, and end the sentence.
      const message = `${line.currency}: ${raw}${/[.!?]$/.test(raw) ? "" : "."}`;
      throw saved.length > 0
        ? new PartialAddError(`${message} ${saved.join(", ")} ${saved.length > 1 ? "were" : "was"} added before this.`, saved)
        : new Error(message);
    }
  }
}

/**
 * Changing a line's amount or its rate. Only what changed is sent. A changed
 * rate needs a reason and a person allowed to change rates; core says so when
 * not, and an unset ceiling needs a senior approver.
 */
export async function updateForexLine(
  lineId: string,
  data: { fxAmount?: number; rateOverride?: number; reason?: string },
) {
  try {
    const { data: result } = await moneefloApi.patch(`${base()}/lines/${encodeURIComponent(lineId)}`, {
      ...(data.fxAmount !== undefined ? { fxAmount: data.fxAmount } : {}),
      ...(data.rateOverride !== undefined
        ? { rateOverride: data.rateOverride, rateOverrideReason: data.reason?.trim() }
        : {}),
    });
    return result;
  } catch (error) {
    throw coreError(error, "The line could not be changed.");
  }
}

/** The LRS declaration (Form A2) has been signed. */
export async function markLrsSigned() {
  try {
    const { data } = await moneefloApi.patch(`${base()}/forex`, { lrsSigned: true });
    return data;
  } catch (error) {
    throw coreError(error, "The LRS declaration could not be recorded.");
  }
}

/* ------------------------------------------------------------ documents -- */

/** Mark an order's documents verified (needs `document.verify`). */
export async function verifyForexDocuments(ids: string[]) {
  try {
    for (const id of ids) {
      await moneefloApi.post(`/documents/${encodeURIComponent(id)}/verify`, { status: "verified" });
    }
  } catch (error) {
    throw coreError(error, "The document could not be approved.");
  }
}

/** Reject a document. The reason is always asked for and goes to core with the decision. */
export async function rejectForexDocument(id: string, reason: string) {
  try {
    await moneefloApi.post(`/documents/${encodeURIComponent(id)}/verify`, {
      status: "rejected",
      reason: reason.trim(),
    });
  } catch (error) {
    throw coreError(error, "The document could not be rejected.");
  }
}

/**
 * File a document on the order on screen. Core fills a request in only when
 * the upload names the requirement it answers, so `requirementId` goes when the
 * document list says which one it is.
 */
export async function uploadForexOrderDocument(data: {
  file: File;
  docType: string;
  requirementId?: string | null;
  /** The document this one replaces. */
  replacesDocumentId?: string | null;
  /** The request document this upload answers; core closes the request. */
  requestId?: string | null;
}) {
  const form = new FormData();
  form.append("file", data.file);
  form.append("docType", data.docType.trim().toLowerCase().slice(0, 40) || "other");
  if (data.requirementId) form.append("requirementId", data.requirementId);
  if (data.replacesDocumentId) form.append("replacesDocumentId", data.replacesDocumentId);
  if (data.requestId) form.append("requestId", data.requestId);
  try {
    const { data: result } = await moneefloApi.post(`${base()}/documents`, form);
    return result;
  } catch (error) {
    throw coreError(error, "The document could not be uploaded.");
  }
}

/** Ask the customer for a document that is not in yet. */
export async function requestForexDocument(data: { docType: string; label?: string; requirementId?: string | null }) {
  try {
    const { data: result } = await moneefloApi.post(`${base()}/documents/request`, {
      docType: data.docType.trim().toLowerCase().slice(0, 40) || "other",
      label: data.label?.trim() || undefined,
      requirementId: data.requirementId || undefined,
    });
    return result;
  } catch (error) {
    throw coreError(error, "The document could not be requested.");
  }
}

/* ------------------------------------------------------------------ KYC -- */

const KYC_LABEL: Record<string, string> = {
  pan: "PAN",
  ckyc: "CKYC",
  aadhaar_ekyc: "Aadhaar e-KYC",
  video_kyc: "Video KYC",
  kyb: "KYB",
  passport: "Passport",
};

type KycHistory = {
  documentsOnFile: { docType: string; last4: string | null; verifiedAt: string | null }[];
  checks: { id: string; type: string; provider: string | null; status: string; checkedAt: string | null; recordedBy?: string | null; reason?: string | null }[];
};

/** The customer's KYC checks, newest first, in the rows the history table reads. */
export async function getForexKycHistory(customerId: string) {
  try {
    const { data } = await moneefloApi.get<KycHistory>(`/customers/${encodeURIComponent(customerId)}/kyc`);
    return data.checks.map((c) => ({
      id: c.id,
      created_at: c.checkedAt ?? "",
      doc_name: `${KYC_LABEL[c.type] ?? c.type}${c.provider ? ` (${c.provider})` : ""}`,
      status: c.status === "passed" ? "verified" : c.status.replace(/_/g, " "),
      // Staff see who recorded a check by hand and why.
      recorded_by: c.recordedBy ?? "",
      reason: c.reason ?? "",
    }));
  } catch (error) {
    throw coreError(error, "The KYC history could not be loaded.");
  }
}

export type CustomerDocumentRow = { id: string; docType: string; fileName: string | null; label: string | null; status: string; downloadable?: boolean };

/** Every document filed on the customer, for choosing evidence. */
export async function listCustomerDocuments(customerId: string) {
  try {
    const { data } = await moneefloApi.get<CustomerDocumentRow[]>(`/customers/${encodeURIComponent(customerId)}/documents`);
    return data;
  } catch (error) {
    throw coreError(error, "The customer's documents could not be loaded.");
  }
}

/** File a new document on the customer (not on the order), and give back its id. */
export async function uploadCustomerDocument(customerId: string, file: File, docType: string) {
  const form = new FormData();
  form.append("file", file);
  form.append("docType", docType);
  try {
    const { data } = await moneefloApi.post<{ id: string }>(`/customers/${encodeURIComponent(customerId)}/documents`, form);
    return data;
  } catch (error) {
    throw coreError(error, "The evidence could not be uploaded.");
  }
}

/**
 * The desk's manual approval of the identity papers on file (needs
 * `kyc.review`), done by hand because the automatic check did not (PLT-53):
 * a passed manual check is recorded for each PAN or passport not yet verified,
 * with the reason and the evidence document the person gives. Reading a scan
 * automatically is a different thing and is not offered here.
 */
export type KycCheckWaiting = "pan" | "passport";

/** The identity papers on the customer that no check has passed yet, which is what the desk approves by hand. */
export async function listKycWaiting(customerId: string): Promise<KycCheckWaiting[]> {
  try {
    const { data } = await moneefloApi.get<KycHistory>(`/customers/${encodeURIComponent(customerId)}/kyc`);
    return data.documentsOnFile
      .filter((d) => ["pan", "passport"].includes(d.docType) && !d.verifiedAt)
      .map((d) => d.docType as KycCheckWaiting);
  } catch (error) {
    throw coreError(error, "The KYC checks could not be loaded.");
  }
}

/** Core refused one check; `check` says which, so the screen can mark that check's field. */
export class KycCheckError extends Error {
  check: KycCheckWaiting;
  constructor(message: string, check: KycCheckWaiting) {
    super(message);
    this.name = "KycCheckError";
    this.check = check;
  }
}

export async function approveForexKyc(
  customerId: string,
  proof: { reason: string; evidence: Partial<Record<KycCheckWaiting, string>> },
) {
  try {
    const entries = Object.entries(proof.evidence) as [KycCheckWaiting, string][];
    if (entries.length === 0) {
      throw new Error("There is no PAN or passport waiting to be approved.");
    }
    for (const [type, evidenceDocumentId] of entries) {
      try {
        await moneefloApi.post(`/customers/${encodeURIComponent(customerId)}/kyc`, {
          type,
          provider: "manual",
          status: "passed",
          reason: proof.reason.trim(),
          evidenceDocumentId,
        });
      } catch (error) {
        throw new KycCheckError(coreError(error, "The KYC could not be approved.").message, type);
      }
    }
  } catch (error) {
    if (error instanceof KycCheckError) throw error;
    throw coreError(error, "The KYC could not be approved.");
  }
}
